import { AddressError } from '@components/Address/AddressError';
import { NoServiceOffered } from '@components/Address/NoServiceOffered';
import { SavedAddressList } from '@components/Address/SavedAddressList';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BOOKING_AVAILABLE_COUNTRY_CODES, BookingAddress } from 'noddi-types';
import { NoddiLink, usePaginationQueryParam } from 'noddi-ui-web';
import { BookingDetailsTabs, customerRoutes } from 'noddi-util';
import {
  AddressRecoveryForm,
  AddressSearch,
  getLongAddressName,
  useAddressResolution,
  useAuthContext,
  useShouldFireRequestForLoggedInUser
} from 'noddi-web';
import { useNavigate } from 'react-router-dom';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../../utils/commonTranslations';

type AddressPickerProps = {
  setSelectedAddress: (address?: BookingAddress) => void;
  selectedAddress?: BookingAddress;
  addressId: number;
  bookingSlug: string;
  hideSavedAddresses: boolean;
  loading: boolean;
};

const initialPageSize = 5;

export const EditAddressPicker = ({
  setSelectedAddress,
  selectedAddress,
  addressId,
  bookingSlug,
  hideSavedAddresses,
  loading
}: AddressPickerProps) => {
  const { currentUserGroupId: userGroupId } = useAuthContext();
  const shouldFireRequest = useShouldFireRequestForLoggedInUser();
  const navigate = useNavigate();
  const { currentBrand } = useCurrentBrand();
  const { pagination, setPagination } = usePaginationQueryParam({
    initialPageSize,
    paramKeyPrefix: 'saved-addresses'
  });
  const {
    data: savedAddresses,
    isPending: isSavedAddressesPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupAddressesValidForNewBooking,
    input: { userGroupId: userGroupId!, brandDomain: currentBrand, ...pagination },
    queryConfig: { enabled: shouldFireRequest, staleTime: Infinity }
  });

  const {
    recoveryFormProps,
    resolve: createAddressFromPlaceId,
    clear: resetCreateAddressFromPlaceId,
    isPending,
    error: addressError,
    data: createdAddress
  } = useAddressResolution({
    onSuccess: ({ isInDeliveryArea, ...rest }) => {
      if (addressId === rest.id) {
        navigate(customerRoutes.bookingInfo.getPath({ slug: bookingSlug, tab: BookingDetailsTabs.ORDER_DETAILS }));
        return;
      }
      if (isInDeliveryArea) {
        setSelectedAddress(rest);
      } else {
        setSelectedAddress(undefined);
      }
    },
    onError: () => {
      setSelectedAddress(undefined);
    }
  });

  if (isSavedAddressesPending) {
    return <NoddiLoadingScreenWithTrans />;
  }

  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  const displayAddress = selectedAddress ?? createdAddress?.data;
  const savedResults = savedAddresses?.results ?? [];
  const savedAddressNames = hideSavedAddresses ? [] : savedResults.map((address) => getLongAddressName(address));

  const selectSavedAddress = (savedAddress: (typeof savedResults)[number]) => {
    resetCreateAddressFromPlaceId();
    if (addressId === savedAddress.id) {
      navigate(customerRoutes.bookingInfo.getPath({ slug: bookingSlug, tab: BookingDetailsTabs.ORDER_DETAILS }));
      return;
    }
    setSelectedAddress(savedAddress);
  };

  const isAddressOutOfDeliveryArea = createdAddress?.data && !createdAddress.data.isInDeliveryArea;

  return (
    <div className='flex flex-col gap-4'>
      {!hideSavedAddresses && !displayAddress && (
        <SavedAddressList
          items={savedResults.map((address) => ({
            id: address.id,
            addressName: getLongAddressName(address)
          }))}
          onSelect={(id) => {
            const savedAddress = savedResults.find((address) => address.id === id);
            if (savedAddress) {
              selectSavedAddress(savedAddress);
            }
          }}
          pagination={{
            pagination,
            loading: isSavedAddressesPending,
            totalCount: savedAddresses?.count ?? 0,
            onChange: ({ pageIndex, pageSize }) => setPagination({ pageIndex, pageSize, search: '' })
          }}
        />
      )}
      <AddressSearch
        countryCodes={BOOKING_AVAILABLE_COUNTRY_CODES}
        // force a re-mount when selectedAddress changes to reset default value, when picking a saved address from the list
        key={`booking-edit-address-picker-${selectedAddress?.id ?? 'no-address'} `}
        translations={getCommonTranslations().addressSearch}
        savedAddressNames={savedAddressNames}
        onSelect={(addressSuggestion) => {
          setSelectedAddress(undefined);
          createAddressFromPlaceId({ placeId: addressSuggestion.placeId, brandDomains: [currentBrand] });
        }}
        onClear={() => {
          resetCreateAddressFromPlaceId();
          setSelectedAddress(undefined);
        }}
        loading={isPending || loading}
        defaultValue={displayAddress}
      />
      {recoveryFormProps ? (
        <AddressRecoveryForm {...recoveryFormProps} translations={getCommonTranslations().addressSearch.recovery} />
      ) : (
        <AddressError addressError={addressError} />
      )}

      {isAddressOutOfDeliveryArea && (
        <>
          <NoServiceOffered
            translations={{
              noServiceOfferedNotice: getCommonTranslations().noServiceOfferedNotice
            }}
            address={createdAddress.data}
          />
          <NoddiLink startIcon='Map' target='_blank' to={customerRoutes.deliveryMap.getPath()}>
            <Trans>See map of where we currently deliver</Trans>
            <span className='sr-only'> ({t`opens in a new tab`})</span>
          </NoddiLink>
        </>
      )}
    </div>
  );
};
